import React, { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import PropTypes from "prop-types";
import ReactDom from "react-dom";
import ComboHeader from "../ComboHeader/ComboHeader";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Modal,
  Tooltip,
  styled,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CircleLoader from "../Loaders/CircleLoader";

export const ButtonWrapper = styled("div")({
  button: {
    alignItems: "center",
    backgroundColor: "#fff",
    borderRadius: "6px",
    display: "flex",
    padding: "8px",
    span: {
      color: "#475257",
      fontSize: "10px",
      fontWeight: "800",
      letterSpacing: "0.15em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
    },
    i: {
      color: "#475257",
      fontSize: "14px",
    },
    "&:hover": {
      backgroundColor: "#f2f4fd",
      span: { color: "#0190d2" },
      i: { color: "#0190d2" },
    },
  },
});

export const LoaderWrapper = styled("div")({
  "& img": {
    maxWidth: "18px",
    maxHeight: "16px",
  },
});

function Card({
  app,
  children,
  header,
  style,
  className,
  expandable,
  detailButton,
  detailContent,
  absoluteExpandButton,
  cardContentStyle,
  exportTable,
  exportTableId,
  exportContainer,
}) {
  const notFullHd = useMediaQuery({ query: "(max-width: 1600px)" });
  const [isExpanded, setExpand] = useState(false);
  const mounted = useRef();
  const [loadingExcel, setLoadingExcel] = useState(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const handleExpand = () => {
    if (mounted.current) {
      setExpand(!isExpanded);
    }
  };

  const exportToXLS = async (objectId) => {
    setLoadingExcel(false);
    let id = objectId;

    if (exportContainer) {
      await app.visualization
        .get(objectId)
        .then((vis) => {
          id = vis.model.items.activeId;
        })
        .catch(() => setLoadingExcel(false));
    }

    if (app && id) {
      setLoadingExcel(true);
      await app.visualization
        .get(id)
        .then((vis) => {
          vis.table.exportData({ format: "OOXML", state: "A" }).then((link) => {
            setLoadingExcel(false);
            const xlsLink = `https://qsense.mtrix.com.br${
              process.env.REACT_APP_PREFIX
            }${link.substring(link.indexOf("tempcontent"))}`;
            window.open(xlsLink);
          });
        })
        .catch(() => setLoadingExcel(false));
    }
  };

  const [openModal, setOpenModal] = useState(false);

  const Content = () => (
    <div
      className={isExpanded ? "expandedCardContainer" : `${className} card`}
      style={style}
    >
      <div
        className={isExpanded ? "expandedCardContent" : "cardContentArea"}
        style={cardContentStyle}
      >
        <Grid
          container
          flexDirection={"column"}
          justifyContent={"space-between"}
          alignItems={"flex-start"}
        >
          <Grid
            className="cardHeader"
            item
            container
            justifyContent={header ? "space-between" : "flex-end"}
            alignItems={"flex-start"}
          >
            {header && (
              <ComboHeader
                app={app}
                reference={{
                  title: {
                    id: header.titleId,
                    hard: header.hardTitle,
                  },
                  subtitle: {
                    id: header.subtitle,
                    hard: header.hardSubtitle,
                  },
                }}
                tooltip={header.tooltip}
                legends={header.legends}
                radio={header.radio}
                classStyle={header.classStyle}
                scale={header.scale}
              />
            )}
            {expandable && (
              <div
                className={
                  absoluteExpandButton
                    ? "primaryButton expandButton absolute"
                    : "primaryButton expandButton"
                }
              >
                <button type="button" onClick={() => handleExpand()}>
                  <span>{isExpanded ? "Reduzir" : "Ampliar"}</span>
                  <i className="fal fa-expand" />
                </button>
              </div>
            )}
            {exportTable && (
              <div
                style={{
                  display: "flex",

                  justifyContent: "flex-start",
                }}
              >
                <Tooltip title="Exportar para Excel">
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => exportToXLS(exportTableId)}
                    endIcon={<FileDownloadOutlinedIcon />}
                    className="export-button"
                  >
                    {!loadingExcel ? (
                      <Box component={"span"}></Box>
                    ) : (
                      <LoaderWrapper>
                        <CircleLoader />
                      </LoaderWrapper>
                    )}
                  </Button>
                </Tooltip>
              </div>
            )}
            {detailButton && (
              <Grid
                item
                className="cardRedirectButton primaryButton expandButton"
              >
                <Button
                  variant="text"
                  endIcon={<OpenInNewIcon />}
                  onClick={() => setOpenModal(true)}
                >
                  <Box component={"span"}>Detalhes</Box>
                </Button>
                <Modal open={openModal} onClose={() => setOpenModal(false)}>
                  <Grid
                    className="card"
                    container
                    flexDirection={"column"}
                    justifyContent={"center"}
                    sx={{
                      position: "relative",
                      top: notFullHd ? "20%" : "30%",
                      left: "12%",
                      width: "80%",
                    }}
                  >
                    <Grid
                      container
                      item
                      flexDirection={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      sx={{
                        width: "100%",
                        maxWidth: "100%",
                      }}
                    >
                      <Grid item xs>
                        <ComboHeader
                          reference={{
                            title: {
                              hard: "Detalhes",
                            },
                          }}
                        />
                      </Grid>
                      <Grid
                        item
                        container
                        flexDirection={"row"}
                        justifyContent={"flex-end"}
                        alignItems={"flex-start"}
                        xs
                        sx={{
                          paddingBottom: "18px",
                        }}
                      >
                        <Tooltip title="Exportar para Excel">
                          <Button
                            type="button"
                            variant="outlined"
                            onClick={() => exportToXLS(exportTableId)}
                            endIcon={<FileDownloadOutlinedIcon />}
                            className="export-button"
                          >
                            {!loadingExcel ? (
                              <Box component={"span"}></Box>
                            ) : (
                              <LoaderWrapper>
                                <CircleLoader />
                              </LoaderWrapper>
                            )}
                          </Button>
                        </Tooltip>
                        <ButtonWrapper>
                          <Tooltip title="Fechar">
                            <IconButton
                              type="button"
                              onClick={() => setOpenModal(false)}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                        </ButtonWrapper>
                      </Grid>
                    </Grid>
                    <Grid
                      className="cardContentArea"
                      item
                      sx={{ height: "100%" }}
                      xs
                    >
                      {detailContent}
                    </Grid>
                  </Grid>
                </Modal>
              </Grid>
            )}
          </Grid>
          {children}
        </Grid>
      </div>
    </div>
  );
  return isExpanded ? (
    ReactDom.createPortal(
      <Content />,
      document.getElementById("expandedWrapper")
    )
  ) : (
    <Content />
  );
}

Card.defaultProps = {
  app: null,
  children: null,
  header: null,
  style: null,
  className: "",
  expandable: false,
  detailButton: false,
  absoluteExpandButton: false,
  cardContentStyle: {},
  exportTable: false,
  exportTableId: "",
  exportContainer: false,
};

Card.propTypes = {
  app: PropTypes.object,
  children: PropTypes.node,
  header: PropTypes.object,
  style: PropTypes.object,
  className: PropTypes.string,
  expandable: PropTypes.bool,
  detailButton: PropTypes.bool,
  absoluteExpandButton: PropTypes.bool,
  cardContentStyle: PropTypes.object,
  exportTable: PropTypes.bool,
  exportTableId: PropTypes.string,
  exportContainer: PropTypes.bool,
};

export default Card;
